import {
  Component,
  ChangeDetectionStrategy,
  input,
  signal,
  computed,
  forwardRef,
  effect
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-default-value-input',
  imports: [MatIconModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DefaultValueInputComponent),
      multi: true
    }
  ],
  template: `
    @switch (fieldType()) {
      @case ('boolean') {
        <!-- Toggle -->
        <button
          type="button"
          role="switch"
          [attr.aria-checked]="booleanValue()"
          [attr.aria-label]="'Default value toggle'"
          (click)="toggleBoolean()"
          [disabled]="disabled()"
          class="relative inline-flex h-9 w-16 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          [class.bg-blue-600]="booleanValue() === true"
          [class.bg-zinc-200]="booleanValue() !== true"
          [class.dark:bg-zinc-600]="booleanValue() !== true"
          [class.opacity-50]="disabled()"
          [class.cursor-not-allowed]="disabled()"
        >
          <span
            class="pointer-events-none block h-7 w-7 rounded-full bg-white shadow-lg ring-0 transition-transform"
            [class.translate-x-7]="booleanValue() === true"
            [class.translate-x-0]="booleanValue() !== true"
          ></span>
        </button>
        <span class="ml-2 text-xs text-zinc-500 dark:text-zinc-400">
          {{ booleanValue() === null ? 'None' : (booleanValue() ? 'True' : 'False') }}
          @if (booleanValue() !== null) {
            <button
              type="button"
              (click)="clearBoolean()"
              class="ml-1 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
            >
              <mat-icon class="!text-xs !w-3 !h-3 align-middle">close</mat-icon>
            </button>
          }
        </span>
      }

      @case ('number') {
        <!-- Number input -->
        <input
          type="text"
          inputmode="decimal"
          pattern="[0-9]*([.,][0-9]*)?"
          [value]="stringValue()"
          (input)="onNumberInput($event)"
          (blur)="onNumberInput($event, true)"
          [disabled]="disabled()"
          placeholder="0"
          class="flex h-9 w-full rounded-md border bg-white dark:bg-zinc-800 dark:text-zinc-100 px-3 py-1.5 text-sm shadow-sm transition-colors placeholder:text-zinc-400 dark:placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:border-transparent disabled:cursor-not-allowed disabled:opacity-50"
          [class.border-red-300]="isError()"
          [class.border-zinc-200]="!isError()"
          [class.dark:border-zinc-600]="!isError()"
          [class.focus:ring-red-500]="isError()"
          [class.focus:ring-blue-500]="!isError()"
        >
      }

      @case ('string') {
        <!-- String input -->
        <input
          type="text"
          [value]="stringValue()"
          (input)="onStringInput($event)"
          [disabled]="disabled()"
          placeholder="Default value"
          class="flex h-9 w-full rounded-md border bg-white dark:bg-zinc-800 dark:text-zinc-100 px-3 py-1.5 text-sm shadow-sm transition-colors placeholder:text-zinc-400 dark:placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:border-transparent disabled:cursor-not-allowed disabled:opacity-50"
          [class.border-red-300]="isError()"
          [class.border-zinc-200]="!isError()"
          [class.dark:border-zinc-600]="!isError()"
          [class.focus:ring-red-500]="isError()"
          [class.focus:ring-blue-500]="!isError()"
        >
      }

      @case ('string-list') {
        <!-- String chips -->
        <div
          class="flex flex-wrap gap-2 rounded-md border bg-white dark:bg-zinc-800 px-3 py-2 text-sm shadow-sm focus-within:ring-2 focus-within:border-transparent transition-all min-h-[38px]"
          [class.border-red-300]="isError()"
          [class.border-zinc-200]="!isError()"
          [class.dark:border-zinc-600]="!isError()"
          [class.focus-within:ring-red-500]="isError()"
          [class.focus-within:ring-blue-500]="!isError()"
          [class.opacity-50]="disabled()"
        >
          @for (chip of chipsValue(); track chip) {
            <span class="inline-flex items-center gap-1 rounded-md bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 text-xs font-medium text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
              {{ chip }}
              @if (!disabled()) {
                <button
                  type="button"
                  (click)="removeChip(chip)"
                  class="rounded-full hover:bg-blue-100 dark:hover:bg-blue-800 p-0.5"
                >
                  <mat-icon class="!text-xs !w-3 !h-3 text-blue-500 dark:text-blue-400">close</mat-icon>
                </button>
              }
            </span>
          }
          @if (!disabled()) {
            <input
              type="text"
              [value]="chipInputValue()"
              (input)="onChipInputChange($event)"
              (keydown)="onChipKeyDown($event)"
              [placeholder]="chipsValue().length === 0 ? 'Type and press Enter...' : ''"
              class="flex-1 bg-transparent outline-none placeholder:text-zinc-400 dark:placeholder:text-zinc-500 dark:text-zinc-100 min-w-[120px] py-0.5"
            >
          }
        </div>
        <p class="text-xs text-zinc-500 dark:text-zinc-400 mt-1">Press Enter to add values</p>
      }

      @case ('number-list') {
        <!-- Number chips -->
        <div
          class="flex flex-wrap gap-2 rounded-md border bg-white dark:bg-zinc-800 px-3 py-2 text-sm shadow-sm focus-within:ring-2 focus-within:border-transparent transition-all min-h-[38px]"
          [class.border-red-300]="isError()"
          [class.border-zinc-200]="!isError()"
          [class.dark:border-zinc-600]="!isError()"
          [class.focus-within:ring-red-500]="isError()"
          [class.focus-within:ring-blue-500]="!isError()"
          [class.opacity-50]="disabled()"
        >
          @for (chip of chipsValue(); track chip) {
            <span class="inline-flex items-center gap-1 rounded-md bg-amber-50 dark:bg-amber-900/30 px-2 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800">
              {{ chip }}
              @if (!disabled()) {
                <button
                  type="button"
                  (click)="removeChip(chip)"
                  class="rounded-full hover:bg-amber-100 dark:hover:bg-amber-800 p-0.5"
                >
                  <mat-icon class="!text-xs !w-3 !h-3 text-amber-500 dark:text-amber-400">close</mat-icon>
                </button>
              }
            </span>
          }
          @if (!disabled()) {
            <input
              type="number"
              step="any"
              [value]="chipInputValue()"
              (input)="onChipInputChange($event)"
              (keydown)="onChipKeyDown($event)"
              [placeholder]="chipsValue().length === 0 ? 'Type number and press Enter...' : ''"
              class="flex-1 bg-transparent outline-none placeholder:text-zinc-400 dark:placeholder:text-zinc-500 dark:text-zinc-100 min-w-[120px] py-0.5"
            >
          }
        </div>
        <p class="text-xs text-zinc-500 dark:text-zinc-400 mt-1">Press Enter to add values</p>
      }

      @default {
        <!-- Fallback text input -->
        <input
          type="text"
          [value]="stringValue()"
          (input)="onStringInput($event)"
          [disabled]="disabled()"
          placeholder="Default value"
          class="flex h-9 w-full rounded-md border bg-white dark:bg-zinc-800 dark:text-zinc-100 px-3 py-1.5 text-sm shadow-sm transition-colors placeholder:text-zinc-400 dark:placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:border-transparent disabled:cursor-not-allowed disabled:opacity-50"
          [class.border-red-300]="isError()"
          [class.border-zinc-200]="!isError()"
          [class.dark:border-zinc-600]="!isError()"
          [class.focus:ring-red-500]="isError()"
          [class.focus:ring-blue-500]="!isError()"
        >
      }
    }
  `,
  styles: [`
    :host {
      display: block;
    }
  `]
})
export class DefaultValueInputComponent implements ControlValueAccessor {
  fieldType = input<string>('string');
  isError = input<boolean | undefined>(false);

  disabled = signal(false);
  private rawValue = signal<string | number | boolean | string[] | number[] | null>(null);

  chipInputValue = signal('');

  stringValue = computed(() => {
    const val = this.rawValue();
    if (val === null || val === undefined) return '';
    if (Array.isArray(val)) return val.join(', ');
    return String(val);
  });

  booleanValue = computed(() => {
    const val = this.rawValue();
    if (val === null || val === undefined) return null;
    if (typeof val === 'boolean') return val;
    if (val === 'true') return true;
    if (val === 'false') return false;
    return null;
  });

  chipsValue = computed<string[]>(() => {
    const val = this.rawValue();
    if (Array.isArray(val)) return val.map(v => String(v));
    return [];
  });

  private onChange: (value: string | number | boolean | string[] | number[] | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor() {
    effect(() => {
      const type = this.fieldType();
      const val = this.rawValue();
      // Reset chip input when type changes
      this.chipInputValue.set('');
    });
  }

  writeValue(value: string | number | boolean | string[] | number[] | null): void {
    this.rawValue.set(value);
  }

  registerOnChange(fn: (value: string | number | boolean | string[] | number[] | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  toggleBoolean(): void {
    if (this.disabled()) return;
    const current = this.booleanValue();
    let next: boolean;
    if (current === null) {
      next = true;
    } else {
      next = !current;
    }
    this.rawValue.set(next);
    this.onChange(next);
    this.onTouched();
  }

  clearBoolean(): void {
    if (this.disabled()) return;
    this.rawValue.set(null);
    this.onChange(null);
    this.onTouched();
  }

  onNumberInput(event: Event, blur = false): void {
    const input = event.target as HTMLInputElement;
    const val = input.value;
    if (val === '') {
      this.rawValue.set(null);
      this.onChange(null);
    } else {
      const num = Number(val);
      if (isNaN(num)) return;
      this.rawValue.set(num);
      this.onChange(num);
    }
    this.onTouched();
  }

  onStringInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const val = input.value;
    if (val === '') {
      this.rawValue.set(null);
      this.onChange(null);
    } else {
      this.rawValue.set(val);
      this.onChange(val);
    }
    this.onTouched();
  }

  onChipInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.chipInputValue.set(input.value);
  }

  onChipKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      const val = this.chipInputValue().trim();
      if (!val) return;

      const type = this.fieldType();
      const currentChips = this.chipsValue();

      if (type === 'number-list') {
        const normalized = val.replace(/,/g, '.');
        const num = Number(normalized);
        if (isNaN(num)) return;
        const numChips = currentChips.map(Number);
        if (!numChips.includes(num)) {
          const newArr = [...numChips, num];
          this.rawValue.set(newArr);
          this.onChange(newArr);
        }
      } else {
        if (!currentChips.includes(val)) {
          const newArr = [...currentChips, val];
          this.rawValue.set(newArr);
          this.onChange(newArr);
        }
      }
      this.chipInputValue.set('');
      // Clear the actual input element
      const input = event.target as HTMLInputElement;
      input.value = '';
      this.onTouched();
    } else if (event.key === 'Backspace' && !this.chipInputValue()) {
      const currentChips = this.chipsValue();
      if (currentChips.length > 0) {
        const type = this.fieldType();
        const newChips = currentChips.slice(0, -1);
        if (type === 'number-list') {
          const numArr = newChips.map(Number);
          this.rawValue.set(numArr);
          this.onChange(numArr);
        } else {
          this.rawValue.set(newChips);
          this.onChange(newChips);
        }
        this.onTouched();
      }
    }
  }

  removeChip(chip: string): void {
    if (this.disabled()) return;
    const type = this.fieldType();
    const currentChips = this.chipsValue().filter(c => c !== chip);

    if (currentChips.length === 0) {
      this.rawValue.set(type === 'number-list' ? [] : []);
      this.onChange(type === 'number-list' ? [] : []);
    } else if (type === 'number-list') {
      const numArr = currentChips.map(Number);
      this.rawValue.set(numArr);
      this.onChange(numArr);
    } else {
      this.rawValue.set(currentChips);
      this.onChange(currentChips);
    }
    this.onTouched();
  }
}
